import { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useAddWorker, type RateSchedule, type TMWorker } from "@/hooks/usePersonnelProjects";
import { useCreateVendor } from "@/hooks/useVendors";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Loader2, Search, UserPlus } from "lucide-react";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  personnelProjectId: string;
  rates: RateSchedule[];
  /** IDs of workers already on the project (to exclude from suggestions) */
  existingWorkerNames?: Set<string>;
  /** Called after a worker is successfully created and added to the project */
  onWorkerAdded?: (worker: TMWorker) => void;
}

export function AddWorkerDialog({ open, onOpenChange, personnelProjectId, rates, existingWorkerNames, onWorkerAdded }: Props) {
  const addWorker = useAddWorker();
  const createVendor = useCreateVendor();
  const qc = useQueryClient();
  const { user } = useAuth();
  const [tab, setTab] = useState<string>("vendor");
  const [name, setName] = useState("");
  const [rateId, setRateId] = useState("");
  const [startDate, setStartDate] = useState(new Date().toISOString().split("T")[0]);
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [vendorId, setVendorId] = useState<string | null>(null);
  const [searchQ, setSearchQ] = useState("");
  const [workerType, setWorkerType] = useState<"vendor" | "team">("vendor");
  const [isSaving, setIsSaving] = useState(false);

  const activeRates = rates.filter((r) => r.is_active);

  // Fetch vendors
  const { data: vendors = [] } = useQuery({
    queryKey: ["vendors_for_worker_select"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("vendors")
        .select("id, name, email, phone, contact_name")
        .eq("is_deleted", false)
        .order("name");
      if (error) throw error;
      return data ?? [];
    },
    enabled: open && !!user,
  });

  // Fetch team members
  const { data: teamMembers = [] } = useQuery({
    queryKey: ["team_members_for_worker_select"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_profiles")
        .select("id, display_name, first_name, last_name, email, phone")
        .eq("status", "active");
      if (error) throw error;
      return data ?? [];
    },
    enabled: open && !!user,
  });

  // Filter by search
  const filteredVendors = useMemo(() => {
    const q = searchQ.toLowerCase().trim();
    if (!q) return vendors;
    return vendors.filter((v: any) =>
      v.name?.toLowerCase().includes(q) ||
      v.contact_name?.toLowerCase().includes(q) ||
      v.email?.toLowerCase().includes(q)
    );
  }, [vendors, searchQ]);

  const filteredTeam = useMemo(() => {
    const q = searchQ.toLowerCase().trim();
    if (!q) return teamMembers;
    return teamMembers.filter((m: any) => {
      const displayName = m.display_name || `${m.first_name ?? ""} ${m.last_name ?? ""}`.trim();
      return displayName.toLowerCase().includes(q) || m.email?.toLowerCase().includes(q);
    });
  }, [teamMembers, searchQ]);

  const reset = () => {
    setName("");
    setRateId("");
    setStartDate(new Date().toISOString().split("T")[0]);
    setPhone("");
    setEmail("");
    setVendorId(null);
    setSearchQ("");
    setWorkerType("vendor");
  };

  const selectVendor = (v: any) => {
    setName(v.name);
    setEmail(v.email ?? "");
    setPhone(v.phone ?? "");
    setVendorId(v.id);
    setWorkerType("vendor");
    setTab("details");
  };

  const selectTeamMember = (m: any) => {
    const displayName = m.display_name || `${m.first_name ?? ""} ${m.last_name ?? ""}`.trim();
    setName(displayName);
    setEmail(m.email ?? "");
    setPhone(m.phone ?? "");
    setVendorId(null);
    setWorkerType("team");
    setTab("details");
  };

  const handleSave = async () => {
    if (!name.trim()) return toast.error("Worker name is required");
    if (!rateId) return toast.error("Select a role");

    setIsSaving(true);
    try {
      let finalVendorId = vendorId;

      // If creating a brand-new worker (not selected from existing list)
      if (!vendorId && workerType === "vendor") {
        // Create a new vendor record
        const newVendor = await createVendor.mutateAsync({
          name: name.trim(),
          email: email.trim() || undefined,
          phone: phone.trim() || undefined,
          vendor_types: ["general"],
          is_1099_eligible: true,
        });
        finalVendorId = newVendor.id;
        qc.invalidateQueries({ queryKey: ["vendors"] });
      }

      // Create the tm_worker on the project
      const result = await addWorker.mutateAsync({
        personnel_project_id: personnelProjectId,
        worker_name: name.trim(),
        rate_schedule_id: rateId,
        start_date: startDate || undefined,
        phone: phone.trim() || undefined,
        portal_email: email.trim() || undefined,
      });

      // If we have a vendor ID, link it (useAddWorker doesn't support vendor_id directly, update after)
      if (finalVendorId && result) {
        await supabase
          .from("tm_workers" as any)
          .update({ vendor_id: finalVendorId })
          .eq("id", (result as any).id);
      }

      toast.success("Worker added");

      // Notify parent so they can auto-add to timesheet
      if (onWorkerAdded && result) {
        const workerData: TMWorker = {
          id: (result as any).id,
          company_id: (result as any).company_id,
          personnel_project_id: personnelProjectId,
          rate_schedule_id: rateId,
          worker_name: name.trim(),
          vendor_id: finalVendorId,
          is_active: true,
          start_date: startDate || null,
          end_date: null,
          created_at: new Date().toISOString(),
        };
        onWorkerAdded(workerData);
      }

      reset();
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setIsSaving(false);
    }
  };

  const alreadyExists = (workerName: string) => existingWorkerNames?.has(workerName.toLowerCase().trim()) ?? false;
  const isPending = isSaving || addWorker.isPending || createVendor.isPending;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" /> Add Worker
          </DialogTitle>
          <DialogDescription>Select from vendors or team members, or enter manually.</DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="w-full">
            <TabsTrigger value="vendor" className="flex-1">Vendors</TabsTrigger>
            <TabsTrigger value="team" className="flex-1">Team Members</TabsTrigger>
            <TabsTrigger value="details" className="flex-1">Details</TabsTrigger>
          </TabsList>

          {/* Search bar for vendor/team tabs */}
          {(tab === "vendor" || tab === "team") && (
            <div className="relative mt-3">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder={tab === "vendor" ? "Search vendors…" : "Search team…"}
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                className="pl-9"
              />
            </div>
          )}

          <TabsContent value="vendor" className="max-h-[300px] overflow-y-auto space-y-1 mt-2">
            {filteredVendors.length === 0 ? (
              <p className="text-center py-6 text-muted-foreground text-sm">No vendors found</p>
            ) : (
              filteredVendors.map((v: any) => {
                const exists = alreadyExists(v.name);
                return (
                  <button
                    key={v.id}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${exists ? "opacity-40 cursor-not-allowed" : "hover:bg-accent cursor-pointer"}`}
                    onClick={() => !exists && selectVendor(v)}
                    disabled={exists}
                  >
                    <div className="font-medium">{v.name}</div>
                    {(v.contact_name || v.email) && (
                      <div className="text-xs text-muted-foreground">
                        {[v.contact_name, v.email].filter(Boolean).join(" · ")}
                      </div>
                    )}
                    {exists && <span className="text-xs text-muted-foreground italic">Already on project</span>}
                  </button>
                );
              })
            )}
          </TabsContent>

          <TabsContent value="team" className="max-h-[300px] overflow-y-auto space-y-1 mt-2">
            {filteredTeam.length === 0 ? (
              <p className="text-center py-6 text-muted-foreground text-sm">No team members found</p>
            ) : (
              filteredTeam.map((m: any) => {
                const displayName = m.display_name || `${m.first_name ?? ""} ${m.last_name ?? ""}`.trim() || m.email;
                const exists = alreadyExists(displayName);
                return (
                  <button
                    key={m.id}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${exists ? "opacity-40 cursor-not-allowed" : "hover:bg-accent cursor-pointer"}`}
                    onClick={() => !exists && selectTeamMember(m)}
                    disabled={exists}
                  >
                    <div className="font-medium">{displayName}</div>
                    {m.email && <div className="text-xs text-muted-foreground">{m.email}</div>}
                    {exists && <span className="text-xs text-muted-foreground italic">Already on project</span>}
                  </button>
                );
              })
            )}
          </TabsContent>

          <TabsContent value="details" className="space-y-4 mt-2">
            {/* Worker type selector - only show when creating a new worker (no vendorId selected) */}
            {!vendorId && (
              <div className="space-y-2 rounded-md border p-3 bg-muted/30">
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Add as</Label>
                <RadioGroup
                  value={workerType}
                  onValueChange={(v) => setWorkerType(v as "vendor" | "team")}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="vendor" id="wt-vendor" />
                    <Label htmlFor="wt-vendor" className="text-sm cursor-pointer">
                      Vendor
                      <span className="block text-xs text-muted-foreground">Creates a new vendor record</span>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="team" id="wt-team" />
                    <Label htmlFor="wt-team" className="text-sm cursor-pointer">
                      Team Member
                      <span className="block text-xs text-muted-foreground">Internal employee</span>
                    </Label>
                  </div>
                </RadioGroup>
              </div>
            )}

            <div className="space-y-1.5">
              <Label>Worker Name *</Label>
              <Input placeholder="Full name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>

            <div className="space-y-1.5">
              <Label>Role *</Label>
              <Select value={rateId} onValueChange={setRateId}>
                <SelectTrigger><SelectValue placeholder="Select role" /></SelectTrigger>
                <SelectContent>
                  {activeRates.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.role_name} — ${r.bill_rate_regular}/hr
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>Start Date</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Phone</Label>
                <Input placeholder="(555) 123-4567" value={phone} onChange={(e) => setPhone(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input placeholder="worker@email.com" value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={isPending || !name.trim() || !rateId}>
            {isPending ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" />Adding…</> : "Add Worker"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
